import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CircleDollarSign, AlertTriangle, Trash2, Plus, Check } from "lucide-react";
import api from "@/lib/api";
import TaskDialog from "@/components/TaskDialog";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

export default function BillsPanel({ onChange }) {
  const [bills, setBills] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/tasks").then((r) => { setBills(r.data.filter((t) => t.kind === "bill")); onChange && onChange(); }).catch(() => {});
  }, [onChange]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (b) => { await api.put(`/tasks/${b.id}`, { completed: !b.completed }); load(); };
  const remove = async (b) => { await api.delete(`/tasks/${b.id}`); toast.success("Bill removed"); load(); };
  const openEdit = (b) => { setEditing(b); setDialog(true); };
  const openNew = () => { setEditing(null); setDialog(true); };

  return (
    <section data-testid="snap-bills" className="bg-[#0d1730] border border-emerald-500/20 rounded-2xl p-5 sm:p-6 shadow-2xl shadow-emerald-950/40">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-bold tracking-wide text-emerald-300 flex items-center gap-2">
          <CircleDollarSign className="w-5 h-5" /> Bill Payments
        </h2>
        <button data-testid="snap-add-bill" onClick={openNew} className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-500 text-[#04140c] text-sm font-semibold hover:bg-emerald-400 transition-colors">
          <Plus className="w-4 h-4" /> Add bill
        </button>
      </div>

      {bills.length === 0 && <p className="text-sm text-slate-500 py-6 text-center">No bills tracked yet. Add one to see it on your Pulse calendar.</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {bills.map((b) => {
          const d = b.days_until_due;
          const soon = d != null && d <= 3;
          const badge = d == null ? null
            : d < 0 ? { t: "Overdue", c: "#f87171" }
            : d === 0 ? { t: "Due today", c: "#f87171" }
            : d === 1 ? { t: "Due tomorrow", c: "#fbbf24" }
            : d <= 3 ? { t: `Due in ${d} days`, c: "#fbbf24" }
            : { t: `Due in ${d} days`, c: "#64748b" };
          return (
            <div key={b.id} data-testid="snap-bill-row" className="group rounded-xl border border-white/10 bg-[#0a1226]/70 p-4"
              style={soon ? { boxShadow: `inset 3px 0 0 ${badge.c}` } : undefined}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    {soon && <AlertTriangle className="w-3.5 h-3.5 shrink-0 animate-pulse" style={{ color: badge.c }} />}
                    <p className={`font-medium truncate ${b.completed ? "line-through text-slate-500" : "text-slate-100"}`}>{b.title}</p>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5 font-mono">{b.recurrence_label}</p>
                  {badge && <span className="mt-2 inline-block text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ color: badge.c, border: `1px solid ${badge.c}55` }}>{badge.t}</span>}
                </div>
                <span className="font-mono text-emerald-300 shrink-0">{money(b.amount)}</span>
              </div>
              <div className="flex items-center gap-1 mt-3">
                <button data-testid="snap-bill-paid" onClick={() => toggle(b)} className={`flex items-center gap-1 h-7 px-2 text-[11px] rounded border transition-colors ${b.completed ? "border-emerald-500 text-emerald-400" : "border-white/15 text-slate-400 hover:border-emerald-500/60"}`}>
                  <Check className="w-3 h-3" /> {b.completed ? "Paid" : "Mark paid"}
                </button>
                <button data-testid="snap-bill-edit" onClick={() => openEdit(b)} className="h-7 px-2 text-[11px] rounded border border-white/15 text-slate-300 hover:border-emerald-500/60 transition-colors">Edit</button>
                <button data-testid="snap-bill-delete" onClick={() => remove(b)} className="w-7 h-7 grid place-items-center rounded border border-white/15 text-slate-400 hover:border-rose-500/60 hover:text-rose-400 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <TaskDialog open={dialog} onClose={() => setDialog(false)} onSaved={load} task={editing} initialKind="bill" />
    </section>
  );
}
